import { BaseGQLService } from '../../../common/services/BaseGQLService';
import { FilterOperator, SorterSet } from '../../../common/types/gql-types';
import { CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { ModelType } from '../../../common/utils/GQLIdEncoder';
import { UserLevel } from '../enums/UserLevel';
import { UserRole } from '../enums/UserRole';
import { UserMapper } from '../mappers/UserMapper';
import { User } from '../models/User';

const USER_FRAGMENT = `
id
name
preferredName
level
origin
roles
`;

export const GET_ORGANIZATION_USERS_QUERY = `
query (
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filters: UsersFilter,
  $sorters: [UsersSorter],
) {
  viewer {
    organization {
      users(
        first: $first
        last: $last
        after: $after
        before: $before
        filters: $filters
        sorters: $sorters
      ) {
        totalCount
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
        edges {
          node {
            ${USER_FRAGMENT}
          }
        }
      }
    }
  }
}
`;

export const CREATE_USER_QUERY = `
mutation($name: String!, $preferredName: String, $level: UserLevel, $roles: [Role!], $password: String!) {
  createLocalUser(input: { name: $name, preferredName: $preferredName, level: $level, roles: $roles, password: $password }) {
    user {
      ${USER_FRAGMENT}
    }
  }
}
`;

export const UPDATE_USER_QUERY = `
mutation($id: ID!, $preferredName: String, $level: UserLevel, $roles: [Role!]) {
  updateUser(input: { userId: $id, preferredName: $preferredName, level: $level, roles: $roles }) {
    user {
      ${USER_FRAGMENT}
    }
  }
}
`;

export const DELETE_USER_QUERY = `
mutation($id: ID!) {
  deleteUser(input: { userId: $id }) {
    __typename
  }
}
`;

export const CHANGE_PASSWORD_QUERY = `
mutation($currentPassword: String!, $newPassword: String!) {
  changePassword(input: { currentPassword: $currentPassword, newPassword: $newPassword }) {
    __typename
  }
}
`;

export const RESET_PASSWORD_QUERY = `
mutation($id: ID!) {
  resetUserPassword(input: { userId: $id }) {
    password
  }
}
`;

type CreateUserPayload = {
  name: string;
  preferredName: string | null;
  password: string;
} & ({ level: null; roles: UserRole[] } | { level: UserLevel; roles: null });

type UpdateUserPayload = {
  userId: number;
  preferredName: string | null;
} & ({ level: null; roles: UserRole[] | null } | { level: UserLevel | null; roles: null });

type ChangePasswordPayload = {
  currentPassword: string;
  newPassword: string;
};

export class OrganizationUsersRepository extends BaseGQLService {
  private readonly mapper = new UserMapper();

  async getUsers(cursor: CursorInfo, filters: FilterOperator | null, sorters: SorterSet[]): Promise<PaginationResult<User>> {
    return this.query(
      GET_ORGANIZATION_USERS_QUERY,
      {
        ...this.mapCursorInfo(cursor),
        filters,
        sorters: this.getSorters(sorters),
      },
      (data) => {
        const { edges, pageInfo, totalCount } = data.viewer.organization.users;
        return {
          data: edges.map(({ node }: any) => this.mapper.mapGQLModel(node)),
          totalCount,
          pageInfo,
        };
      }
    );
  }

  async createUser({ name, preferredName, level, roles, password }: CreateUserPayload): Promise<User> {
    return this.mutate(
      CREATE_USER_QUERY,
      {
        name,
        preferredName,
        level,
        roles,
        password,
      },
      (data) => this.mapper.mapGQLModel(data.createLocalUser.user)
    );
  }

  async updateUser({ userId, preferredName, level, roles }: UpdateUserPayload): Promise<User> {
    return this.mutate(
      UPDATE_USER_QUERY,
      {
        id: this.gqlIdEncoder.encode(ModelType.User, userId),
        preferredName,
        level,
        roles,
      },
      (data) => this.mapper.mapGQLModel(data.updateUser.user)
    );
  }

  async deleteUser(userId: number): Promise<void> {
    return this.mutate(DELETE_USER_QUERY, {
      id: this.gqlIdEncoder.encode(ModelType.User, userId),
    });
  }

  async changePassword({ currentPassword, newPassword }: ChangePasswordPayload): Promise<string> {
    return this.mutate(
      CHANGE_PASSWORD_QUERY,
      {
        currentPassword,
        newPassword,
      },
      () => newPassword
    );
  }

  async resetPassword(userId: number): Promise<string> {
    return this.mutate(
      RESET_PASSWORD_QUERY,
      {
        id: this.gqlIdEncoder.encode(ModelType.User, userId),
      },
      (data) => data.resetUserPassword.password
    );
  }

  /**
   * Users are returned by the API sorted by ascending ID (oldest-newest).
   * Unless explicit sorters are requested, sort users so that the current user is first, then
   * all other users from newest to oldest.
   *
   * @param sorters the requested sorters
   * @returns the requested sorters if any, otherwise the default sorter
   */
  private getSorters(sorters: SorterSet[]): SorterSet[] {
    if (sorters.length > 0) {
      return sorters;
    }
    return [
      { field: 'current', order: 'ASC' },
      { field: 'id', order: 'DESC' },
    ];
  }
}
